import {ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {inject} from '@angular/core';
import {AuthStatusService} from './services/auth-status.service';

/**
 * Baut eine neue authGuard-Funktion samt eigenem Warmup-Zustand auf. `authGuard` weiter unten
 * ist die für app.routes.ts bestimmte Singleton-Instanz; die Factory existiert daneben, damit
 * Tests pro Testfall einen frischen, unverbrauchten Warmup-Zustand bekommen - genau wie zuvor
 * TestBed.inject(AuthGuard) pro Test eine neue Klasseninstanz (und damit einen neuen
 * Konstruktor-Aufruf) erzeugte.
 */
export function createAuthGuard(): CanActivateFn {
    // Ersetzt den früheren Konstruktor-Seiteneffekt der Klasse: da eine Funktion anders als eine
    // DI-Singleton-Instanz kein "einmal pro Objekt" kennt, hält dieser Closure-State fest, ob der
    // Warmup-Check für diese Guard-Instanz schon ausgelöst wurde.
    let warmedUp = false;

    return async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> => {
        const router = inject(Router);
        const authService = inject(AuthStatusService);

        if (!warmedUp) {
            warmedUp = true;
            authService.checkStatus().catch(() => {});
        }

        // getStatus() startet mit false und wird erst asynchron (Socket/Polling) auf den
        // echten Wert gesetzt. Ein bereits true gewertetes Signal ist zuverlässig (wurde
        // schon durch eine echte Prüfung bestätigt) - ein false könnte aber auch bedeuten
        // "noch nicht geprüft". Nur im false-Fall holen wir daher einen frischen, auf das
        // Ergebnis wartenden Check nach, statt dem Default-Wert blind zu vertrauen.
        const authenticated = authService.getStatus()() || (await authService.checkStatus());

        if (authenticated) {
            if (state.url === '/login') {
                return router.createUrlTree(['']);
            }
            return true;
        }

        if (state.url === '/login') {
            return true;
        }
        // UrlTree statt window.open("/login", "_self"): kein harter Seiten-Reload, der die
        // gesamte Angular-App-Instanz (inkl. WebSocket-Verbindung) zerstören würde - Angular
        // navigiert stattdessen innerhalb derselben Seite zu /login.
        return router.createUrlTree(['/login']);
    };
}

export const authGuard: CanActivateFn = createAuthGuard();
